import { ABBREVIATED_DAYS_LOCALIZED_STRINGS } from '../../../../constants'
import { hourToLocalizedLabel } from './kegCollectionFormUtils'

// One memo-friendly date formatter factory
export const makeDateFormatter = locale =>
  new Intl.DateTimeFormat(locale || undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

export const formatDate = (value, formatter, includeTime = false) => {
  if (!value) return ''
  const d = new Date(value)
  if (includeTime) {
    return isNaN(d.valueOf())
      ? ''
      : `${formatter.format(d)} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }
  return isNaN(d.valueOf()) ? '' : formatter.format(d)
}

const getMostRecentCollectionRequest = (collectionRequests = []) =>
  collectionRequests.reduce(
    (latest, request) => (!latest || request.dateCreated > latest.dateCreated ? request : latest),
    null
  )

const getKegCollectionQuantity = (collectionRequests = []) => {
  if (!collectionRequests.length) return 0
  const mostRecent = getMostRecentCollectionRequest(collectionRequests)
  const list = mostRecent?.kcRequestItemQtyObjectSet || []
  return list.reduce((acc, cur) => acc + (cur?.quantity || 0), 0)
}

const getPreferredPickup = (collectionRequests = [], translate) => {
  const t = translate || (s => s)
  const mostRecent = getMostRecentCollectionRequest(collectionRequests)
  if (!mostRecent) return t('bookingHasNoRequests')

  const {
    startHour,
    endHour,
    venueOpenMonday,
    venueOpenTuesday,
    venueOpenWednesday,
    venueOpenThursday,
    venueOpenFriday,
    venueOpenSaturday,
    venueOpenSunday,
  } = mostRecent

  const flagsToKeys = [
    [venueOpenMonday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[0].monday],
    [venueOpenTuesday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[1].tuesday],
    [venueOpenWednesday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[2].wednesday],
    [venueOpenThursday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[3].thursday],
    [venueOpenFriday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[4].friday],
    [venueOpenSaturday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[5].saturday],
    [venueOpenSunday, ABBREVIATED_DAYS_LOCALIZED_STRINGS[6].sunday],
  ]

  const daysOpen = flagsToKeys.filter(([f]) => f).map(([, key]) => t(key))

  const start = hourToLocalizedLabel(startHour)
  const end = hourToLocalizedLabel(endHour)
  const hoursRange = start && end ? `${start} - ${end}` : ''

  return `${hoursRange} ${daysOpen.join(', ')}`.trim()
}

const userInfoDefaults = {
  columnText: '',
  firstName: null,
  lastName: null,
  id: null,
  userName: null,
  cognitoSubId: null,
  emailAddress: null,
}

export const getCreatedBy = (booking, translate) => {
  if (booking.createdBy && booking.createdByUser?.id) {
    const { firstName, lastName, id, username, cognitoSubId, emailAddress } = booking.createdByUser
    return {
      columnText: `${firstName} ${lastName}`,
      firstName,
      lastName,
      id,
      userName: username,
      cognitoSubId,
      emailAddress,
    }
  }

  return { ...userInfoDefaults, columnText: translate('venueApp') }
}

export const getAssignedBy = (booking, translate) => {
  if (booking.assignedBy && booking.assignedByUser?.id) {
    const { firstName, lastName, id, username, cognitoSubId, emailAddress } = booking.assignedByUser
    return {
      columnText: `${firstName} ${lastName}`,
      firstName,
      lastName,
      id,
      userName: username,
      cognitoSubId,
      emailAddress,
    }
  }

  const isBookingAssignedToCollector = Boolean(booking.collectorCustomerId)
  const isAutoAssigned = isBookingAssignedToCollector && !booking.assignedBy

  if (isAutoAssigned) {
    return { ...userInfoDefaults, columnText: translate('autoAssigned') }
  }

  return userInfoDefaults
}

export const formatBookingsTableRows = (bookings, translate, dateFormatter) =>
  bookings.map(booking => {
    const {
      customerName = '',
      addressObject,
      kegCollectionRequestObjects,
      dateCreated,
      collectorCustomerId,
      collectorCustomerName,
      collectorCustomerTapId,
      collectorCustomerSubsidiaryId,
      startEstimatedPickupDate,
      endEstimatedPickupDate,
      bookingStatus,
      id,
      actualPickupDate,
      lastKegPickupDate,
      venueNotes,
      preferredCollectorId,
      phoneNumber,
      bookingReferenceId,
    } = booking || {}

    const dateRequestedTs = dateCreated ? new Date(dateCreated).getTime() : 0
    const startEstimatedPickupTs = startEstimatedPickupDate
      ? new Date(startEstimatedPickupDate).getTime()
      : 0
    const endEstimatedPickupTs = endEstimatedPickupDate
      ? new Date(endEstimatedPickupDate).getTime()
      : 0

    return {
      phoneNumber,
      bookingAssignedBy: getAssignedBy(booking, translate).columnText,
      bookingCreatedBy: getCreatedBy(booking, translate).columnText,
      bookingId: id,
      bookingReferenceId: bookingReferenceId || '',
      collectorCustomerId,
      collectorCustomerName,
      collectorCustomerTapId,
      collectorCustomerSubsidiaryId,
      preferredCollectorId,
      customerName,
      state: addressObject?.stateOrProvince || '',
      postalCode: addressObject?.postalCode || '',
      preferredPickup: getPreferredPickup(kegCollectionRequestObjects || [], translate),
      dateRequested: formatDate(dateCreated, dateFormatter, true),
      actualPickupDate: formatDate(actualPickupDate, dateFormatter),
      startEstimatedPickupDate: formatDate(startEstimatedPickupDate, dateFormatter),
      endEstimatedPickupDate: formatDate(endEstimatedPickupDate, dateFormatter),
      lastKegPickupDate: formatDate(lastKegPickupDate, dateFormatter),
      kegQuantity: getKegCollectionQuantity(kegCollectionRequestObjects || []),
      assignedStatus: Boolean(collectorCustomerId),
      bookingStatus,
      bookingNotes:
        getMostRecentCollectionRequest(kegCollectionRequestObjects || [])?.pickupInstruction || '',
      mostRecentCollectionRequestId:
        getMostRecentCollectionRequest(kegCollectionRequestObjects || [])?.id || '',
      venueNotes: venueNotes || '',
      kegCollectionRequestObjects,
      addressObject,
      // Extra fields for sorting purposes only (not rendered):
      _sort_customerName: (customerName || '').toLowerCase(),
      _sort_dateRequestedTs: dateRequestedTs,
      _sort_startEstimatedPickupDateTs: startEstimatedPickupTs,
      _sort_endEstimatedPickupDateTs: endEstimatedPickupTs,
      _sort_kegQuantity: getKegCollectionQuantity(kegCollectionRequestObjects || []),
      _sort_state: (addressObject?.stateOrProvince || '').toLowerCase(),
      _sort_postcode: (addressObject?.postalCode || '').toLowerCase(),
      _sort_bookingStatus: (bookingStatus || '').toLowerCase(),
      bookingRawData: { ...booking },
    }
  })

const quantitiesConfig = {
  L20: 0,
  L30: 0,
  G9: 0,
  L50: 0,
}

export const formatKegCollectionTableRows = (kegCollectionsArray, dateFormatter) =>
  kegCollectionsArray.map(kegCollectionObj => {
    const { id, kcRequestItemQtyObjectSet, dateCreated } = kegCollectionObj
    const quantitiesColumnData = kcRequestItemQtyObjectSet.reduce(
      (acc, collection) => {
        if (!acc[collection.containerType]) {
          acc[collection.containerType] = collection.quantity
          acc.total += collection.quantity
        }
        return acc
      },
      { total: 0 }
    )
    return {
      collectionId: id,
      kegCollectionObj,
      dateRequested: formatDate(dateCreated, dateFormatter, true),
      ...quantitiesConfig,
      ...quantitiesColumnData,
      // Extra fields for sorting purposes only (not rendered):
      _sort_dateRequested: dateCreated ? new Date(dateCreated).getTime() : 0,
    }
  })

const formatPhoneNumberForCSV = phoneNumber => {
  if (!phoneNumber) return ''

  return `="${phoneNumber.toString().trim()}"`
}

export const formatBookingsCSVExportData = (bookings, translate, locale, collectors) => {
  // add assigned collector uuid column
  const csvHeaderCells = [
    translate('Reference #'),
    translate('createdBy'),
    translate('Date Requested'),
    translate('Venue Name'),
    translate('Address'),
    translate('contactPhoneNumber'),
    translate('Preferred Pickup'),
    translate('Quantity'),
    translate('bookingNotes'),
    translate('assignedBy'),
    translate('assignedCollector'),
    `${translate('assignedCollector')} UUID`,
    `${translate('assignedCollector')} TAP ID`,
    translate('preferredCollector'),
    translate('startEstimatedPickupDate'),
    translate('endEstimatedPickupDate'),
    translate('Status'),
  ]

  const formattedBookingData = formatBookingsTableRows(
    bookings,
    translate,
    makeDateFormatter(locale)
  )

  const getCollectorNameById = id => {
    const collector = collectors?.find(col => col.id === id)
    return collector ? collector.name : '-'
  }

  const formatAddress = addressObject => {
    if (!addressObject) return ''

    const { address1, address2, address3, city, stateOrProvince, postalCode, country } =
      addressObject

    const parts = [
      address1,
      address2,
      address3,
      city,
      stateOrProvince && postalCode
        ? `${stateOrProvince} ${postalCode}`
        : stateOrProvince || postalCode,
      country,
    ]

    const cleaned = parts.map(p => (typeof p === 'string' ? p.trim() : p)).filter(Boolean)

    return cleaned.join(', ')
  }

  const csvDataRows = formattedBookingData.map(booking => [
    booking.bookingReferenceId,
    booking.bookingCreatedBy,
    booking.dateRequested,
    booking.customerName,
    formatAddress(booking.addressObject),
    booking.phoneNumber ? formatPhoneNumberForCSV(booking.phoneNumber) : '',
    booking.preferredPickup,
    booking.kegQuantity,
    booking.bookingNotes,
    booking.bookingAssignedBy,
    booking.collectorCustomerName || '-',
    booking.collectorCustomerId || '-',
    booking.collectorCustomerTapId || '-',
    getCollectorNameById(booking.preferredCollectorId),
    booking.startEstimatedPickupDate || '-',
    booking.endEstimatedPickupDate || '-',
    translate(booking.bookingStatus),
  ])

  return [csvHeaderCells, ...csvDataRows]
}
